import {
  ACCENT_IDS,
  ACCENT_QUERY_PARAM,
  ACCENT_STORAGE_KEY,
  DEFAULT_ACCENT,
} from "./constants";
import { AccentId } from "./types";

export const isAccentId = (value: unknown): value is AccentId =>
  typeof value === "string" && (ACCENT_IDS as string[]).includes(value);

export const normalizeAccentId = (value: unknown): AccentId =>
  isAccentId(value) ? value : DEFAULT_ACCENT;

/** Previously stored accent ids that were renamed (same as web-app). */
const LEGACY_ACCENT_MAP: Record<string, AccentId> = {
  slackr: "horizon",
};

export const getStoredAccentPreference = (): AccentId => {
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
    if (isAccentId(stored)) {
      return stored;
    }
    if (typeof stored === "string" && LEGACY_ACCENT_MAP[stored]) {
      const migrated = LEGACY_ACCENT_MAP[stored];
      persistAccentPreference(migrated);
      return migrated;
    }
  } catch {
    // Ignore storage access errors
  }
  return DEFAULT_ACCENT;
};

export const persistAccentPreference = (accent: AccentId): void => {
  try {
    localStorage.setItem(ACCENT_STORAGE_KEY, accent);
  } catch {
    // Ignore storage write errors
  }
};

/**
 * Applies the accent id to the document root via `data-accent`.
 * CSS maps accent tokens for light and dark themes.
 */
export const applyDocumentAccent = (accent: AccentId): AccentId => {
  const root = document.documentElement;
  const resolved = isAccentId(accent) ? accent : DEFAULT_ACCENT;
  root.dataset.accent = resolved;
  return resolved;
};

export const getAccentFromSearchParams = (
  search?: string,
): AccentId | null => {
  try {
    const params = new URLSearchParams(
      search ?? (typeof window !== "undefined" ? window.location.search : ""),
    );
    const value = params.get(ACCENT_QUERY_PARAM);
    return isAccentId(value) ? value : null;
  } catch {
    return null;
  }
};

/** Prefer URL accent (from web-app), then localStorage. */
export const getBootstrapAccent = (search?: string): AccentId => {
  return getAccentFromSearchParams(search) || getStoredAccentPreference();
};

export const applyStoredAccent = (): AccentId => {
  return applyDocumentAccent(getBootstrapAccent());
};
